import {
  Box,
  ErrorBanner,
  Loader,
  Space,
  Stack,
} from "@dbn/omnichannel-design-system-g4p";
import { RuntimeLocaleKey, useFormatMessage } from "../../../intl";
import { constNull, pipe } from "fp-ts/function";
import { boolean, eq, option } from "fp-ts";
import { useEffect, useState } from "react";
import { ExpensesConfirmationFormRework } from "./ExpenseConfirmationForm";
import { MoneyAmount } from "../../../globalDomain";
import { Option } from "fp-ts/Option";
import { usePollingEffect, useQuery } from "../../../useAPI";
import * as remoteData from "../../../RemoteData";
import { getBonitaStatus } from "../../api";
import { BonitaStatus, getExpenses, LfDecision } from "./api";
import { CurrentLiabilitiesDialog } from "./CurrentLiabilitiesDialog/CurrentLiabilitiesDialog";
import { ExpensesCalculatorDialog } from "./ExpensesCalculator/ExpensesCalculatorDialog";

export type LfDecisionReceived = Omit<LfDecision, "Pending">;

type Props = {
  onExpensesConfirmationNext: (
    needAdditionalIncomeStep: boolean,
    hasRepaidLoans: boolean,
    lfDecision: LfDecisionReceived,
    regularExpenses: number
  ) => void;
  setIsHeaderReadonly?: (r: boolean) => void;
};

export type SubmitFormState = {
  payAlimony: Option<boolean>;
  alimonyAmount: Option<number>;
  totalRegularMonthlyExpensesNoLiabilities: Option<number>;
  disclaimerCheckbox: boolean;
};

export function ExpensesConfirmation(props: Props) {
  const [submitFormState] = useState<SubmitFormState>({
    payAlimony: option.none as Option<boolean>,
    alimonyAmount: option.none as Option<number>,
    totalRegularMonthlyExpensesNoLiabilities: option.none as Option<number>,
    disclaimerCheckbox: false,
  });
  const [, setShowError] = useState<boolean>(false);
  const [isLiabilitiesDialogOpen, setIsLiabilitiesDialogOpen] = useState(false);
  const [isExpensesDialogOpen, setIsExpensesDialogOpen] = useState(false);
  const formatMessage = useFormatMessage();
  const [readOnly, setReadOnly] = useState(true);
  const [isFormSubmit, setIsFormSubmit] = useState<boolean>(false);
  const [canPoll, setCanPoll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bonitaStatus, setBonitaStatus] = useState<BonitaStatus>("NOT_STARTED");
  const restartPoll = usePollingEffect(getBonitaStatus, {
    intervalMS: 5000,
    disabled: !canPoll,
    shouldPollingContinue: val => val.bonitaStatus === "PENDING",
    onError: () => setShowError(true),
    onSuccess: response => {
      setBonitaStatus(response.bonitaStatus);
      if (
        response.bonitaStatus === "DONE" &&
        option.isSome(response.submitExpensesResponse)
      ) {
        const expensesResponse = response.submitExpensesResponse.value;
        if (expensesResponse.lfDecision !== "Pending") {
          setCanPoll(false);
          props.onExpensesConfirmationNext(
            expensesResponse.needAdditionalIncomeStep,
            response.hasRepaidLoans,
            expensesResponse.lfDecision,
            pipe(
              submitFormState.totalRegularMonthlyExpensesNoLiabilities,
              option.fold(
                () => 0,
                value => value
              )
            )
          );
        }
      }
      if (response.bonitaStatus === "ERROR") {
        setShowError(true);
      }
    },
  });

  const [expenses, refreshExpenses] = useQuery(getExpenses);

  useEffect(() => {
    if (isFormSubmit) {
      setReadOnly(true);
      setBonitaStatus("PENDING");
      setCanPoll(true);
    }
  }, [isFormSubmit]);

  useEffect(() => {
    if (props.setIsHeaderReadonly) {
      props.setIsHeaderReadonly(readOnly);
    }
  }, [readOnly]);

  useEffect(() => {
    if (canPoll) {
      restartPoll();
    }
  }, [canPoll]);

  const foldAlimonyAmount: (
    formStateAlimonyAmount: Option<number>,
    remoteAlimonyAmount: Option<MoneyAmount>
  ) => Option<number> = (formStateAlimonyAmount, remoteAlimonyAmount) =>
    pipe(
      formStateAlimonyAmount,
      option.fold(
        () =>
          pipe(
            remoteAlimonyAmount,
            option.filterMap(v =>
              pipe(
                v.amount,
                option.fromPredicate(amount => !eq.eqNumber.equals(amount, 0))
              )
            )
          ),
        () => formStateAlimonyAmount
      )
    );

  const foldPayAlimony: (
    formStatePayAlimony: Option<boolean>,
    remotePayAlimony: Option<boolean>
  ) => Option<boolean> = (formStatePayAlimony, remotePayAlimony) => {
    return pipe(
      formStatePayAlimony,
      option.fold(
        () => remotePayAlimony,
        () => formStatePayAlimony
      )
    );
  };

  const updateLoading = (newVal: boolean) =>
    newVal !== loading && setLoading(newVal);
  pipe(
    expenses,
    remoteData.fold(
      () => updateLoading(true),
      () => updateLoading(false),
      (_, loading) => updateLoading(loading)
    )
  );

  if (bonitaStatus === "PENDING" || bonitaStatus === "DONE") {
    return (
      <Box hAlignContent="center">
        <Loader
          ariaLabelMessage={formatMessage(
            "generic.loading.alt" as RuntimeLocaleKey
          )}
        />
      </Box>
    );
  }
  return (
    <Box column grow shrink>
      {loading ? (
        <Loader
          ariaLabelMessage={formatMessage(
            "generic.loading.alt" as RuntimeLocaleKey
          )}
        />
      ) : (
        <Stack column grow shrink>
          <Space units={10} />
          {pipe(
            expenses,
            remoteData.fold(
              () => (
                <Loader
                  ariaLabelMessage={formatMessage(
                    "generic.loading.alt" as RuntimeLocaleKey
                  )}
                />
              ),
              () => (
                <Box hAlignContent="center">
                  <ErrorBanner children={formatMessage("GenericError")} />
                </Box>
              ),
              data => (
                <ExpensesConfirmationFormRework
                  onFormSubmit={setIsFormSubmit}
                  readOnly={readOnly}
                  setIsLiabilitiesDialogOpen={setIsLiabilitiesDialogOpen}
                  setIsExpensesDialogOpen={setIsExpensesDialogOpen}
                  submitFormState={{
                    ...submitFormState,
                    totalRegularMonthlyExpensesNoLiabilities: pipe(
                      data.expensesCalculator,
                      option.fold(
                        () => option.none,
                        value =>
                          pipe(
                            value.totalRegularMonthlyExpensesNoLiabilities,
                            option.fold(
                              () => option.none,
                              value => option.some(value.amount)
                            )
                          )
                      )
                    ),
                    alimonyAmount: pipe(
                      pipe(
                        submitFormState.payAlimony,
                        option.fold(
                          () => false,
                          value => value
                        )
                      ),
                      boolean.fold(
                        () =>
                          foldAlimonyAmount(
                            submitFormState.alimonyAmount,
                            data.alimonyExpenses
                          ),
                        () => option.none
                      )
                    ),
                    payAlimony: pipe(
                      submitFormState.payAlimony,
                      option.fold(
                        () =>
                          foldPayAlimony(
                            submitFormState.payAlimony,
                            pipe(
                              data.alimonyExpenses,
                              option.fold(
                                () => option.none,
                                v => option.some(v.amount > 0)
                              )
                            )
                          ),
                        value => option.some(value)
                      )
                    ),
                  }}
                  regularMonthlyExpenses={data.regularMontlyExpenses}
                  calculatorData={data.expensesCalculator}
                  isCalculatorUsed={data.isCalculatorUsed}
                />
              )
            )
          )}
        </Stack>
      )}
      {pipe(
        isLiabilitiesDialogOpen,
        boolean.fold(constNull, () => (
          <CurrentLiabilitiesDialog
            setIsLiabilitiesDialogOpen={isOpen => {
              !isOpen && refreshExpenses();
              setIsLiabilitiesDialogOpen(isOpen);
            }}
          />
        ))
      )}
      {pipe(
        isExpensesDialogOpen,
        boolean.fold(constNull, () =>
          pipe(
            expenses,
            remoteData.fold(constNull, constNull, data =>
              option.isSome(data.expensesCalculator) ? (
                <ExpensesCalculatorDialog
                  onSaved={() => {
                    refreshExpenses();
                    setIsExpensesDialogOpen(false);
                  }}
                  onCancel={() => setIsExpensesDialogOpen(false)}
                  calculatorData={data.expensesCalculator.value}
                />
              ) : null
            )
          )
        )
      )}
    </Box>
  );
}
